import { useEffect, useState } from 'react'
import { Button, Form, ListGroup, Modal, Spinner } from 'react-bootstrap'
import KButton from '../../components/KButton'
import { t } from '../../locale'
import {
  clientNoteCreate,
  clientNoteDelete,
  clientNotesGetByClientId,
} from '../../service/clientNote'
import { ClientNoteEntry, ClientNotesModalProps } from '../../types/clientNote.type'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function ClientNotesModal({ show, clientId, onClose, onNotesChange }: ClientNotesModalProps) {
  const [notes, setNotes] = useState<ClientNoteEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [text, setText] = useState('')

  const loadNotes = async () => {
    try {
      setLoading(true)
      const data = await clientNotesGetByClientId(clientId)
      const result = Array.isArray(data?.notes) ? data.notes : []
      setNotes(result)
      onNotesChange?.(result.length)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!show || !clientId) return
    setText('')
    loadNotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clientId])

  const handleAdd = async () => {
    if (!text.trim()) return

    try {
      setSaving(true)
      const result = await clientNoteCreate(clientId, text.trim())
      if (result?.ok) {
        setText('')
        await loadNotes()
      }
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('deleteNoteConfirm'))) return

    try {
      await clientNoteDelete(id)
      setNotes(prev => {
        const result = prev.filter(note => note._id !== id)
        onNotesChange?.(result.length)
        return result
      })
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <Modal show={show} centered size='lg' onHide={onClose}>
      <Modal.Header className='bg-secondary text-white' closeButton>
        <Modal.Title className='fs-5'>{t('clientNotes')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className='d-flex justify-content-center py-4'>
            <Spinner animation='border' variant='primary' />
          </div>
        )}

        {!loading && notes.length === 0 && (
          <p className='text-muted text-center py-3'>{t('noClientNotes')}</p>
        )}

        {!loading && notes.length > 0 && (
          <ListGroup className='mb-3' style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {notes.map(note => (
              <ListGroup.Item
                key={note._id}
                className='d-flex justify-content-between align-items-start gap-2'
              >
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                  <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {note.text}
                  </div>
                  <small className='text-muted'>
                    {formatDate(note.createdAt)}
                    {note.userEmail ? ` — ${note.userEmail}` : ''}
                  </small>
                </div>
                <KButton
                  icon='trash'
                  variant='outline-danger'
                  title={t('delete')}
                  onClick={() => handleDelete(note._id)}
                />
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        <Form.Group>
          <Form.Control
            as='textarea'
            rows={3}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('clientNotesPlaceholder')}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='outline-primary' disabled={saving || !text.trim()} onClick={handleAdd}>
          {saving ? <Spinner animation='border' size='sm' /> : t('save')}
        </Button>
        <Button variant='outline-secondary' onClick={onClose}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ClientNotesModal
